import { Box, Typography } from '@mui/material';
import { ReactNode, useCallback, useState } from 'react';
import { Control, useController, UseFormSetError } from 'react-hook-form';
import {
  RequiredDocument,
  SunstoneDocument,
} from '../../../../../../../types/api';
import { maxFileSizeInBytes } from '../../../../constants';
import bytesToSize from '../../../../utils/bytesToSize';
import ShowIf from '../../../ConditionalRender/ShowIf';
import SunstoneDocumentFileDropzone from '../../../SunstoneDocumentFileDropzone';
import { InitialValues } from './schema';

const accept = {
  'image/*': [],
  'application/pdf': [],
  'application/doc': ['.doc', '.docx'],
  'application/text': ['.txt'],
};

type Props = {
  name: keyof Pick<InitialValues, 'loan_proposal'>;
  control: Control<InitialValues>;
  setError: UseFormSetError<InitialValues>;
  label?: ReactNode;
  required?: boolean;
  tooltip?: string;
  uploadText?: ReactNode;
  onRemove?: VoidFunction;
  hideMaxFileSize?: boolean;
  onFileChange?: any;
  onUploadResponse?: (
    document: RequiredDocument,
    name: any
  ) => Promise<any | undefined>;
};

export default function SolarProposalFileDropzone({
  name,
  control,
  setError,
  label,
  required,
  tooltip,
  uploadText,
  onRemove,
  onFileChange,
  onUploadResponse,
}: Props) {
  const controller = useController({
    name,
    control,
  });
  const { field, fieldState } = controller;
  const { onChange, value, onBlur } = field;
  const { error, isTouched } = fieldState;

  const [uploading, setUploading] = useState(false);

  const [analysis, setAnalysis] = useState<boolean | null | undefined>(
    undefined
  );
  const inAnalysis = analysis === null;
  const isInvalid = analysis === false;

  return (
    <>
      <SunstoneDocumentFileDropzone
        file={
          value?.file
            ? ({
                name: value.file?.name,
                display_name: value.file?.name,
              } as unknown as SunstoneDocument)
            : undefined
        }
        onDrop={useCallback(
          async (accepted, rejected) => {
            if (accepted.length === 0) return;
            const file = accepted[0];

            setUploading(true);

            try {
              if (onFileChange) {
                await onFileChange(file, name);
              }

              onChange({
                file,
              });
              onBlur();

              // Trigger analysis if we have the uploaded file id
              if (onUploadResponse) {
                const externalId = (file as any)?.external_id;
                if (externalId) {
                  const minimalDoc = {
                    files: [{ id: externalId }],
                  } as unknown as RequiredDocument;

                  try {
                    setAnalysis(null);
                    const result = await onUploadResponse(minimalDoc, name);
                    const recognized =
                      result?.result?.output?.validations?.filter(
                        (v: any) => v.id == 'is_document_recognized'
                      )?.[0]?.result ?? true;
                    setAnalysis(Boolean(recognized));
                  } catch (e: any) {
                    setAnalysis(undefined);
                    console.error('Error triggering analysis:', e.message);
                  }
                } else {
                  setAnalysis(undefined);
                }
              }

              return {
                name: file.name,
                display_name: file.name,
              } as unknown as SunstoneDocument;
            } catch (e: any) {
              const message =
                e?.message ||
                'Upload failed. Please try again or contact support if the problem persists.';
              setError(name, {
                file: {
                  type: 'custom',
                  message,
                },
              } as any);
            } finally {
              setUploading(false);
            }
          },
          [name, onBlur, onChange, onFileChange, setError, onUploadResponse]
        )}
        onDropRejected={useCallback(
          (rejectedFiles) => {
            const file = rejectedFiles[0].file;
            const error = rejectedFiles[0].errors[0];
            let errorCopy: string;
            if (error.code === 'file-too-large') {
              errorCopy = `${file.name} is too big. Max size is ${bytesToSize(
                maxFileSizeInBytes
              )}.`;
            } else if (error.code === 'file-invalid-type') {
              errorCopy = `${file.name} is not a valid file type. Should be an image, PDF, or a text document.`;
            } else {
              errorCopy = error.message;
            }
            setError(name, {
              file: {
                type: 'custom',
                message: errorCopy,
              },
            } as any);
          },
          [name, setError, onUploadResponse]
        )}
        label={label}
        maxSize={maxFileSizeInBytes}
        tooltip={tooltip}
        uploadText={uploadText}
        canReplace
        error={isTouched ? (error as any)?.file?.message : null}
        remove={onRemove}
        required={required}
        accept={accept}
        loading={uploading}
      />
      <ShowIf condition={inAnalysis}>
        <div style={{ color: '#868795', marginTop: '-20px' }}>
          Analyzing file {value?.file?.name?.substr(0, 60)}...
        </div>
      </ShowIf>
      <ShowIf condition={isInvalid}>
        <Box
          sx={{
            paddingLeft: '1rem',
            borderLeft: '4px solid #FFCC00',
            marginTop: '-1rem',
            marginBottom: '1rem',
          }}
        >
          <Typography variant="caption" fontSize="13px">
            This may not be a Solar Proposal Document. Incorrect files can delay
            the review process. <br />
            Proceed if you are sure this file is correct.
          </Typography>
        </Box>
      </ShowIf>
    </>
  );
}
